import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { createFileRoute, Link } from "@tanstack/react-router"
import { Clock, Dumbbell, Play, Plus, Timer } from "lucide-react"
import { DateTime } from "luxon"
import React, { useState } from "react"
import ExerciseForm from "../components/ExerciseForm"
import { Badge } from "../components/ui/badge"
import { Button } from "../components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card"
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog"
import { Input } from "../components/ui/input"
import { Label } from "../components/ui/label"
import { Progress } from "../components/ui/progress"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select"
import { Skeleton } from "../components/ui/skeleton"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table"
import { Textarea } from "../components/ui/textarea"
import { useToast } from "../components/ui/use-toast"
import { useCurrentUserContext } from "../context/useCurrentUserContext"
import type { TUser } from "../hooks/useCurrentUserQuery"
import { useExercises } from "../hooks/useExercises"
import { useActiveWorkoutStore, useStatsStore } from "../store"
import { apiFetch } from "../utils/apiFetch"
// import { diffWorkout } from "../utils/workoutCompare"
import {
  calculateORM,
  calculateTotalWeight,
  displayDuration,
  type TExercise,
  type TExerciseSet,
  type TORMStrategy,
  type TProgram,
  type TWorkout,
  type TWorkoutTemplate,
} from "../utils/workoutUtils"

export const Route = createFileRoute("/_app/workout")({
  component: WorkoutTracker,
})

export default function WorkoutTracker() {
  const queryClient = useQueryClient()
  const { weekStreak, numberOfCompletedWorkouts } = useCurrentUserContext()
  const { stats } = useStatsStore((state) => state)
  const { toast } = useToast()
  const { activeWorkout, setActiveWorkout } = useActiveWorkoutStore((state) => state)
  const [startTime, setStartTime] = useState<DateTime | null>(null)
  //TODO: This should not exist and should instead use activeWorkout.notes
  // const [notes, setNotes] = useState<string>("");
  const [useTemplateDialogOpen, setUseTemplateDialogOpen] = useState<boolean>(false)
  const today = DateTime.local().startOf("day").toFormat("yyyy-MM-dd")
  const [ormSettings, setORMSettings] = useState<{
    weight: number
    reps: number
    strategy: TORMStrategy
  }>({
    weight: 135,
    reps: 5,
    strategy: "Brzycki",
  })
  const [orm, setORM] = useState<number | null>(null)

  const { data: availableWorkouts, isLoading: isLoadingAvailableWorkouts } = useQuery({
    queryKey: ["dailyWorkouts"],
    queryFn: async () => {
      const response = await apiFetch<Array<TWorkout>>(`/v1/workouts?today=${today}`, {
        method: "GET",
      })
      return response
    },
  })

  const { data: reports } = useQuery({
    queryKey: ["reports", DateTime.local().year],
    queryFn: async () => {
      const response = await apiFetch(`/v1/reports/yearEnd/lifts/${DateTime.local().year}`, {
        method: "GET",
      })
      return response as Array<any>
    },
    staleTime: Infinity,
    enabled: DateTime.local().month === 12,
  })

  const { data: weights } = useQuery({
    queryKey: ["weights", DateTime.local().year],
    queryFn: async () => {
      const response = await apiFetch(`/v1/reports/yearEnd/lifts/weights/${DateTime.local().year}`, {
        method: "GET",
      })
      return response as Array<any>
    },
    staleTime: Infinity,
    enabled: DateTime.local().month === 12,
  })

  const { data: averages } = useQuery({
    queryKey: ["averages", DateTime.local().year],
    queryFn: async () => {
      const response = await apiFetch(`/v1/reports/yearEnd/lifts/averages/${DateTime.local().year}`, {
        method: "GET",
      })
      return response as Array<any>
    },
    staleTime: Infinity,
    enabled: DateTime.local().month === 12,
  })

  const { mutateAsync: completeWorkout, isPending: isSavingWorkout } = useMutation({
    mutationFn: async (workout: TWorkout) => {
      await apiFetch("/v1/workouts", {
        method: "POST",
        body: JSON.stringify(workout),
      })

      // Advance the active program if this workout is part of one
      if (workout.workoutTemplateId && activeProgram) {
        await apiFetch("/v1/programs/advance", {
          method: "POST",
          body: JSON.stringify({ workoutId: workout.workoutId }),
        })
      }

      return workout
    },
    onSuccess: async (completedWorkout) => {
      queryClient.setQueryData(["dailyWorkouts"], (oldWorkouts: Array<TWorkout> | undefined) => {
        if (!oldWorkouts) {
          return oldWorkouts
        }
        return oldWorkouts.map((x) => {
          if (x.workoutId === completedWorkout.workoutId) {
            x.dateCompleted = completedWorkout.dateCompleted
          }
          return x
        })
      })
      queryClient.setQueryData(["User"], (oldUserData: TUser | undefined) => {
        if (!oldUserData) {
          return oldUserData
        }
        return {
          ...oldUserData,
          totalWeightLifted: stats.totalWeightLifted,
          totalWeightLiftedThisYear: stats.totalWeightLiftedThisYear,
        }
      })

      setActiveWorkout(null)
      setStartTime(null)
      setElapsedTime(0)

      toast({
        title: "Workout Completed!",
        description: `Great job! You lifted ${calculateTotalWeight(completedWorkout).toLocaleString()} lbs today.`,
      })

      await Promise.allSettled([
        queryClient.refetchQueries({ queryKey: ["User"] }),
        queryClient.refetchQueries({ queryKey: ["dailyWorkouts"] }),
        queryClient.refetchQueries({ queryKey: ["programs"] }),
      ])
    },
    onError: (_error, variables) => {
      console.error("Error saving workout:", JSON.stringify(variables, null, 2))
      toast({
        title: "Workout not saved!",
        description: `uh-oh, sorry for the inconvenience. This product is still in alpha. Please try again or contact me@theaustinwilcox.com for help.`,
      })
    },
  })

  const { data: workoutTemplates = [], isLoading: isLoadingWorkoutTemplates } = useQuery({
    queryKey: ["workoutTemplates"],
    queryFn: async () => {
      const data = await apiFetch<Array<TWorkoutTemplate>>("/v1/workoutTemplates", {
        method: "GET",
      })
      return data
    },
  })

  const { data: activeProgram } = useQuery({
    queryKey: ["programs", "active"],
    queryFn: () => apiFetch<TProgram>("/v1/programs/active"),
  })

  React.useEffect(() => {
    if (activeWorkout && !startTime) {
      setStartTime(DateTime.fromISO(activeWorkout.dateStarted || ""))
    }
  }, [activeWorkout, startTime])

  const [elapsedTime, setElapsedTime] = useState<number>(0)
  React.useEffect(() => {
    let interval: number | undefined

    if (startTime) {
      interval = window.setInterval(() => {
        const now = DateTime.utc()
        const elapsed = Math.floor(now.diff(startTime, "seconds").seconds)
        setElapsedTime(elapsed)
      }, 1000)
    }

    return () => {
      if (interval) {
        clearInterval(interval)
      }
    }
  }, [startTime])

  const formatElapsedTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`
  }

  const { data: exercises } = useExercises()

  /**
   * Handles starting a workout.
   */
  const handleStartWorkout = (workout: TWorkout) => {
    const dateStarted = DateTime.utc()
    setActiveWorkout({
      ...workout,
      exercises: workout.exercises.map((exercise: TExercise) => {
        return {
          ...exercise,
          exerciseId: exercises?.find((ex) => ex.name === exercise.name)?.exerciseId || crypto.randomUUID(),
          sets: exercise.sets.map((set: TExerciseSet) => ({
            ...set,
            setId: crypto.randomUUID(),
          })),
          expanded: true,
        }
      }),
      notes: workout.notes || "",
      dateStarted: dateStarted.toISO()!.toString(),
    })
    setStartTime(DateTime.utc())

    toast({
      title: "Workout Started",
      description: `Get ready to crush your ${workout.name} workout!`,
    })
  }

  /**
   * Handles updating an exercise in the active workout.
   */
  const handleExerciseUpdate = (updatedExercise: TExercise) => {
    if (!activeWorkout) {
      return
    }

    const updatedExercises = activeWorkout.exercises.map((exercise) =>
      exercise.exerciseId === updatedExercise.exerciseId ? updatedExercise : exercise
    )

    const updatedWorkout = {
      ...activeWorkout,
      exercises: updatedExercises,
    }

    setActiveWorkout(updatedWorkout)
  }

  const handleExerciseDelete = (deletedExerciseId: string) => {
    if (!activeWorkout) {
      return
    }

    const newExercises = activeWorkout.exercises.filter((exercise) => exercise.exerciseId !== deletedExerciseId)

    const updatedWorkout = {
      ...activeWorkout,
      exercises: newExercises,
    }

    setActiveWorkout(updatedWorkout)
  }

  /**
   * Handles completing the active workout.
   */
  const handleCompleteWorkout = () => {
    const currentWorkout = useActiveWorkoutStore.getState().activeWorkout
    if (!currentWorkout) {
      return
    }

    if (currentWorkout.exercises.some((ex) => !ex.name || ex.name.trim() === "")) {
      toast({
        title: "No exercises in workout",
        description: "Please add at least one exercise before completing the workout.",
        variant: "destructive",
      })
      return
    }

    const completedWorkout: TWorkout = {
      ...currentWorkout,
      exercises: currentWorkout.exercises.map((exercise: TExercise) => ({
        ...exercise,
        sets: exercise.sets.map((set: TExerciseSet, index: number) => ({
          ...set,
          weight: Number(set.weight),
          reps: Number(set.reps),
          isCompleted: Boolean(set.isCompleted),
          order: set.order || index + 1,
        })),
      })),
      dateCompleted: DateTime.utc().toISO()!.toString(),
    }

    completeWorkout(completedWorkout)
  }

  const handleAddExercise = () => {
    const newExercise: TExercise = {
      exerciseId: crypto.randomUUID(),
      name: "",
      expanded: true,
      sets: [
        {
          setId: crypto.randomUUID(),
          weight: 0,
          reps: 0,
          isCompleted: false,
          order: 1,
        },
      ],
    }

    if (!activeWorkout) {
      console.error("No active workout to add an exercise to")
      return
    }

    setActiveWorkout({
      ...activeWorkout,
      exercises: [...activeWorkout.exercises, newExercise],
    })
  }

  return (
    <div className="space-y-6">
      {!activeWorkout && activeProgram && (
        <Card className="border-primary/50">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <div>
              <CardTitle className="text-sm">Active Program: {activeProgram.name}</CardTitle>
              <p className="text-muted-foreground text-xs">
                Week {activeProgram.currentWeek} of {activeProgram.durationWeeks}
                {activeProgram.workoutTemplates[activeProgram.currentWorkoutIndex] && (
                  <> — Next: {activeProgram.workoutTemplates[activeProgram.currentWorkoutIndex].template.name}</>
                )}
              </p>
            </div>
            <Link to="/programs">
              <Badge variant="outline" className="cursor-pointer">
                View Program
              </Badge>
            </Link>
          </CardHeader>
          <CardContent>
            <Progress
              value={
                (((activeProgram.currentWeek - 1) * activeProgram.workoutTemplates.length +
                  activeProgram.currentWorkoutIndex) /
                  (activeProgram.workoutTemplates.length * activeProgram.durationWeeks)) *
                100
              }
              className="mb-3 h-2"
            />
            <div className="flex gap-2">
              {activeProgram.workoutTemplates[activeProgram.currentWorkoutIndex] && (
                <Button
                  size="sm"
                  onClick={() => {
                    const t = activeProgram.workoutTemplates[activeProgram.currentWorkoutIndex].template
                    // Debug template data and ensure correct handling of exercises
                    console.log("Template data:", t)
                    console.log("Template jsonBlob:", t.jsonBlob)

                    let exercisesFromTemplate: TExercise[] = []
                    if (t.jsonBlob && Array.isArray(t.jsonBlob.exercises)) {
                      exercisesFromTemplate = t.jsonBlob.exercises.map((ex: TExercise) => ({
                        exerciseId: crypto.randomUUID(),
                        name: ex.name,
                        instructions: ex.instructions || "",
                        rpeMin: ex.rpeMin ?? null,
                        rpeMax: ex.rpeMax ?? null,
                        sets:
                          ex.sets?.map((set: TExerciseSet) => ({
                            setId: crypto.randomUUID(),
                            weight: set.weight,
                            reps: set.reps,
                            isCompleted: false,
                            order: set.order || 1,
                          })) || [],
                      }))
                    } else if (t.jsonBlob && typeof t.jsonBlob === "string") {
                      try {
                        const parsed = JSON.parse(t.jsonBlob)
                        if (parsed.exercises && Array.isArray(parsed.exercises)) {
                          exercisesFromTemplate = parsed.exercises.map((ex: TExercise) => ({
                            exerciseId: crypto.randomUUID(),
                            name: ex.name,
                            instructions: ex.instructions || "",
                            rpeMin: ex.rpeMin ?? null,
                            rpeMax: ex.rpeMax ?? null,
                            sets:
                              ex.sets?.map((set: TExerciseSet) => ({
                                setId: crypto.randomUUID(),
                                weight: set.weight,
                                reps: set.reps,
                                isCompleted: false,
                                order: set.order || 1,
                              })) || [],
                          }))
                        }
                      } catch (e) {
                        console.error("Error parsing jsonBlob:", e)
                      }
                    }

                    // Adjust weights for Smolov JR programs based on current week
                    if (activeProgram.notes) {
                      try {
                        const meta = JSON.parse(activeProgram.notes)
                        if (meta.type === "smolov-jr" && meta.weeklyIncrement) {
                          const weekOffset = (activeProgram.currentWeek - 1) * meta.weeklyIncrement
                          if (weekOffset > 0) {
                            exercisesFromTemplate = exercisesFromTemplate.map((ex) => ({
                              ...ex,
                              sets: ex.sets.map((set) => ({
                                ...set,
                                weight: set.weight + weekOffset,
                              })),
                            }))
                          }
                        }
                      } catch {
                        // Not valid JSON or not Smolov JR — use template weights as-is
                      }
                    }

                    handleStartWorkout({
                      workoutId: crypto.randomUUID(),
                      name: t.name,
                      dateScheduled: today,
                      workoutTemplateId: t.workoutTemplateId,
                      exercises: exercisesFromTemplate,
                    })
                  }}
                >
                  <Play className="mr-1 h-4 w-4" />
                  Start Current Workout
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {!activeWorkout ? (
        <>
          <Card className="workout-card">
            <CardHeader>
              <CardTitle>Track Today's Workout</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-4">
                <Button
                  size="sm"
                  onClick={() => {
                    handleStartWorkout({
                      workoutId: crypto.randomUUID(),
                      name: DateTime.local().toFormat("MMM dd, yyyy"),
                      dateScheduled: today,
                      exercises: [
                        {
                          exerciseId: crypto.randomUUID(),
                          name: "",
                          sets: [
                            {
                              setId: crypto.randomUUID(),
                              weight: 0,
                              reps: 0,
                              isCompleted: false,
                              order: 1,
                            },
                          ],
                        },
                      ],
                    })
                  }}
                >
                  Start Workout
                </Button>
                <Dialog
                  open={useTemplateDialogOpen}
                  onOpenChange={(open) => {
                    if (!open) {
                      setUseTemplateDialogOpen(false)
                    }
                  }}
                >
                  <DialogTrigger asChild>
                    <Button
                      size="sm"
                      className={`${workoutTemplates.length === 0 ? "hidden" : ""}`}
                      onClick={() => {
                        setUseTemplateDialogOpen(true)
                      }}
                    >
                      Start Workout From a Template
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Start Workout From a Template</DialogTitle>
                    </DialogHeader>
                    <div className="mt-5 flex max-h-[75vh] flex-col gap-3 overflow-y-auto">
                      {isLoadingWorkoutTemplates
                        ? Array.from({ length: 3 }).map((_, index) => (
                            <Skeleton key={index} className="my-3 block h-12 w-full space-y-4 py-3" />
                          ))
                        : workoutTemplates.map((template: TWorkoutTemplate) => {
                            return (
                              <div key={template.workoutTemplateId} className="flex items-center justify-between py-3">
                                <p>{template.name}</p>
                                <Button
                                  variant="outline"
                                  onClick={() => {
                                    console.log("Template data:", template)
                                    console.log("Template jsonBlob:", template.jsonBlob)

                                    let exercisesFromTemplate: TExercise[] = []
                                    if (template.jsonBlob && Array.isArray(template.jsonBlob.exercises)) {
                                      exercisesFromTemplate = template.jsonBlob.exercises.map(
                                        (exercise: TExercise) => ({
                                          exerciseId: crypto.randomUUID(),
                                          name: exercise.name,
                                          instructions: exercise.instructions || "",
                                          rpeMin: exercise.rpeMin ?? null,
                                          rpeMax: exercise.rpeMax ?? null,
                                          sets: exercise.sets.map((set: TExerciseSet) => ({
                                            setId: crypto.randomUUID(),
                                            weight: set.weight,
                                            reps: set.reps,
                                            isCompleted: false,
                                            order: set.order || 1,
                                          })),
                                        })
                                      )
                                    } else if (template.jsonBlob && typeof template.jsonBlob === "string") {
                                      try {
                                        const parsed = JSON.parse(template.jsonBlob)
                                        if (parsed.exercises && Array.isArray(parsed.exercises)) {
                                          exercisesFromTemplate = parsed.exercises.map((exercise: TExercise) => ({
                                            exerciseId: crypto.randomUUID(),
                                            name: exercise.name,
                                            instructions: exercise.instructions || "",
                                            rpeMin: exercise.rpeMin ?? null,
                                            rpeMax: exercise.rpeMax ?? null,
                                            sets: exercise.sets.map((set: TExerciseSet) => ({
                                              setId: crypto.randomUUID(),
                                              weight: set.weight,
                                              reps: set.reps,
                                              isCompleted: false,
                                              order: set.order || 1,
                                            })),
                                          }))
                                        }
                                      } catch (e) {
                                        console.error("Error parsing jsonBlob:", e)
                                      }
                                    }

                                    handleStartWorkout({
                                      workoutId: crypto.randomUUID(),
                                      name: template.name,
                                      dateScheduled: today,
                                      workoutTemplateId: template.workoutTemplateId,
                                      exercises: exercisesFromTemplate,
                                    })
                                    setUseTemplateDialogOpen(false)
                                  }}
                                >
                                  Start workout
                                </Button>
                              </div>
                            )
                          })}
                    </div>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setUseTemplateDialogOpen(false)}>
                        Cancel
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>
              {isLoadingAvailableWorkouts ? (
                <Skeleton className="my-3 block h-[48px] w-full space-y-4 py-3" />
              ) : (availableWorkouts?.length ?? 0) > 0 ? (
                <div className="space-y-4">
                  {availableWorkouts?.map((workout: TWorkout) => (
                    <div
                      key={workout.workoutId}
                      className="flex flex-col justify-between border-border border-b py-3 last:border-0 sm:flex-row sm:items-center"
                    >
                      <div>
                        <h3 className="font-medium">{workout.name}</h3>
                        <div className="flex flex-wrap items-center gap-x-1 text-muted-foreground text-xs sm:gap-x-2 sm:text-sm">
                          <span className="flex items-center">
                            <Clock size={14} className="mr-1" />
                            {DateTime.fromISO(workout.dateScheduled, {
                              zone: "utc",
                            }).toLocaleString(DateTime.DATE_MED)}
                          </span>
                          <span className="text-muted-foreground">•</span>
                          <span className="flex items-center">
                            <Dumbbell size={14} className="mr-1" />
                            {workout.exercises.length} exercises
                          </span>
                          {workout.dateCompleted && workout.dateStarted && (
                            <>
                              <span className="text-muted-foreground">•</span>
                              <span className="flex items-center">
                                <Timer size={14} className="mr-1" />
                                {displayDuration(workout.dateStarted!, workout.dateCompleted!)}
                              </span>
                            </>
                          )}
                        </div>
                      </div>
                      {!workout.dateCompleted ? (
                        <Button onClick={() => handleStartWorkout(workout)} className="mt-2 w-full sm:mt-0 sm:w-auto">
                          Start Workout
                        </Button>
                      ) : (
                        <div className="mt-1 sm:mt-0">
                          <p className="text-green-500">Completed</p>
                          <p>{calculateTotalWeight(workout).toLocaleString()} lbs lifted</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="py-6 text-center">
                  <Dumbbell size={48} className="mx-auto mb-4 text-muted-foreground" />
                  <h3 className="mb-2 font-medium text-lg">No Workouts Planned</h3>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      ) : (
        <div className="space-y-4">
          <Card className="workout-card">
            <CardHeader>
              <div className="flex flex-col items-start justify-between sm:flex-row sm:items-center">
                <CardTitle>{activeWorkout.name}</CardTitle>
                <div className="mt-2 flex items-center space-x-2 sm:mt-0">
                  <div className="rounded bg-muted px-3 py-1 font-mono text-lg">{formatElapsedTime(elapsedTime)}</div>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {activeWorkout.exercises.map((exercise: TExercise) => (
                  <ExerciseForm
                    key={exercise.exerciseId}
                    exercise={exercise}
                    onUpdate={handleExerciseUpdate}
                    onDelete={handleExerciseDelete}
                    showCompleted
                    type="workout"
                    status="idle"
                  />
                ))}
                <Button type="button" variant="outline" onClick={handleAddExercise} className="mt-2">
                  <Plus size={16} className="mr-2" />
                  Add Exercise
                </Button>

                <div className="pt-2">
                  <label htmlFor="workout-notes" className="mb-1 block font-medium text-sm">
                    Workout Notes
                  </label>
                  <Textarea
                    id="workout-notes"
                    value={activeWorkout.notes || ""}
                    onChange={(e) => {
                      setActiveWorkout({
                        ...activeWorkout,
                        notes: e.target.value,
                      })
                    }}
                    placeholder="How did the workout feel? Any PRs or struggles?"
                    rows={3}
                  />
                </div>

                <div className="mt-4 border-border border-t pt-4">
                  <div className="mb-4 flex items-center justify-between">
                    <div>
                      <h3 className="font-medium text-lg">Workout Summary</h3>
                      <p className="text-muted-foreground">
                        Total weight: {calculateTotalWeight(activeWorkout).toLocaleString()} lbs{" "}
                      </p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <Button
                        variant="outline"
                        disabled={isSavingWorkout}
                        onClick={() => {
                          setActiveWorkout(null)
                        }}
                      >
                        Cancel
                      </Button>
                      <Button disabled={isSavingWorkout} onClick={handleCompleteWorkout}>
                        {isSavingWorkout ? "Saving workout" : "Complete Workout"}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
      <div className="flex flex-col gap-4 md:flex-row md:flex-wrap md:items-baseline md:justify-between">
        <Card className="w-full md:w-[48%]">
          <CardHeader>
            <CardTitle>Stats</CardTitle>
          </CardHeader>
          <CardContent>
            {stats.totalWeightLiftedThisYear !== stats.totalWeightLifted && (
              <p>Total Weight Lifted This Year: {stats.totalWeightLiftedThisYear.toLocaleString()} lbs</p>
            )}
            <p>Total Weight Lifted: {stats.totalWeightLifted.toLocaleString()} lbs</p>
            <p>Week Workout Streak: {weekStreak} week(s)</p>
            <p>Total Number of Completed Workouts: {numberOfCompletedWorkouts}</p>

            {DateTime.local().month === 12 && (
              <>
                <div className="my-5 w-full border-0 border-primary border-t" />
                <p>{DateTime.local().year} Year End Stats</p>
                <p className="mt-5">Favorite Lifts</p>
                <div className="mt-4 max-h-96 w-full overflow-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[200px]">Workout</TableHead>
                        <TableHead className="w-[200px]">Count</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {reports?.map((workout, index) => (
                        <TableRow key={index}>
                          <TableCell className="py-7 font-medium">{workout.name}</TableCell>
                          <TableCell>{workout.count.toLocaleString()}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
                <p className="mt-5">Total Weight Lifted Per Exercise</p>
                <div className="mt-4 max-h-96 w-full overflow-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[200px]">Workout</TableHead>
                        <TableHead className="w-[200px]">Sum of Weights</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {weights?.map((workout, index) => (
                        <TableRow key={index}>
                          <TableCell className="py-7 font-medium">{workout.name}</TableCell>
                          <TableCell>{workout.sumOfWeights.toLocaleString()}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
                <p className="mt-5">Averages</p>
                <div className="mt-4 max-h-96 w-full overflow-auto border-0">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[200px]">Workout</TableHead>
                        <TableHead className="w-[200px]">Average Weight (lbs)</TableHead>
                        <TableHead className="w-[200px]">Average Reps</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {averages?.map((average, index) => (
                        <TableRow key={index}>
                          <TableCell className="py-7 font-medium">{average.name}</TableCell>
                          <TableCell>{average.averageWeight.toLocaleString()}</TableCell>
                          <TableCell>{average.averageReps.toLocaleString()}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </>
            )}
          </CardContent>
        </Card>
        <Card className="w-full md:w-[48%]">
          <CardHeader>
            <CardTitle>One Rep Max Calculator</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col gap-3">
              <div>
                <Label htmlFor="orm-weight" className="font-medium text-sm">
                  Weight
                </Label>
                <Input
                  id="orm-weight"
                  type="text"
                  min="0"
                  value={ormSettings.weight}
                  onChange={(e) => {
                    if (e.target.value === "") {
                      setORMSettings({
                        ...ormSettings,
                        weight: 0,
                      })
                    }
                    if (isNaN(Number(e.target.value))) {
                      return
                    }
                    if (!/^\d+$/.test(e.target.value)) {
                      return
                    }
                    const value = e.target.value.replace(/^0+(?=\d)/, "")

                    setORMSettings({
                      ...ormSettings,
                      weight: parseFloat(value) || 0,
                    })
                  }}
                  className="text-center font-mono text-lg"
                />
              </div>
              <div>
                <Label htmlFor="orm-reps" className="font-medium text-sm">
                  Reps
                </Label>
                <Input
                  id="orm-reps"
                  type="number"
                  min="0"
                  step="1"
                  value={ormSettings.reps}
                  onChange={(e) => {
                    if (e.target.value === "") {
                      setORMSettings({
                        ...ormSettings,
                        reps: 0,
                      })
                    }
                    if (isNaN(Number(e.target.value))) {
                      return
                    }
                    if (!/^\d+$/.test(e.target.value)) {
                      return
                    }
                    const value = e.target.value.replace(/^0+(?=\d)/, "")

                    setORMSettings({
                      ...ormSettings,
                      reps: parseFloat(value) || 0,
                    })
                  }}
                  className="text-center font-mono text-lg"
                />
              </div>
              <div>
                <Label htmlFor="orm-strategy" className="font-medium text-sm">
                  Strategy
                </Label>
                <Select
                  value={ormSettings.strategy}
                  onValueChange={(value: TORMStrategy) => setORMSettings({ ...ormSettings, strategy: value })}
                >
                  <SelectTrigger className="w-[140px]">
                    <SelectValue placeholder="Strategy" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Epley">Epley</SelectItem>
                    <SelectItem value="Brzycki">Brzycki</SelectItem>
                    <SelectItem value="Lombardi">Lombardi</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button
                size="sm"
                onClick={() => {
                  setORM(calculateORM(ormSettings))
                }}
              >
                Calculate One Rep Max
              </Button>
              <p>{orm ? `Estimated ORM: ${orm} lbs` : "Enter weight and reps to calculate 1RM"}</p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
